import { EventEmitter, Injectable, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import * as moment from 'moment';
import {
  BuildingOpeningDay,
  Member,
  Room,
  RoomBooking,
  RoomBookingServiceBooking,
  Service,
  ServiceBooking,
} from '../models';
import { datePastValidator } from '../validators/date-past.directive';
import { outOfOpeningDays } from '../validators/out-of-opening-days.directive';
import { RoomBookingValidators } from '../validators/room-booking-validators.service';
import { ServiceInvoiceData } from '../utils';


@Injectable()
export class RoomBookingFormService {
  public servicesGroupSubmitted: EventEmitter<boolean> = new EventEmitter();

  get existingServiceBookings(): RoomBookingServiceBooking[] {
    return this._existingServiceBookings;
  }
  set existingServiceBookings(bookings: RoomBookingServiceBooking[]) {
    this._existingServiceBookings = bookings;
  }

  set roomAvailability(available: boolean) {
    this._roomNotAvailable = !available;
    this.generalGroup.updateValueAndValidity();
  }

  get otherServiceBookings(): ServiceBooking[] {
    return this._otherServiceBookings;
  }
  set otherServiceBookings(bookings: ServiceBooking[]) {
    this._otherServiceBookings = bookings;
  }

  get owner(): Member {
    return this._owner.value;
  }

  set owner(owner: Member) {
    this._owner.patchValue(owner);
  }

  get referenceOpeningDay(): BuildingOpeningDay[] {
    return this._referenceOpeningDays;
  }
  set referenceOpeningDays(days: BuildingOpeningDay[]) {
    this._referenceOpeningDays = days;
    const datesControls: AbstractControl[] = [this.generalGroup.get('startDate'), this.generalGroup.get('endDate')];

    datesControls.forEach(control => {
      control.setValidators([Validators.required, outOfOpeningDays(this._referenceOpeningDays), datePastValidator()]);
      control.markAsDirty();
      control.updateValueAndValidity({emitEvent: true});
    });
  }

  get referenceRooms(): Room[] {
    return this._referenceRooms;
  }
  set referenceRooms(rooms: Room[]) {
    this._referenceRooms = rooms;
    const room: AbstractControl = this.generalGroup.get('room');
    room.setValidators([Validators.required]);
  }

  get referenceServices(): Service[] {
    return this._referenceServices;
  }
  set referenceServices(services: Service[]) {
    this._referenceServices = services;
  }

  get room(): Room {
    return this._referenceRooms.find(room => room.resourceName === this._room.value);
  }

  set room(room: Room) {
    this._room.patchValue(room.resourceName);
  }
  generalGroup: FormGroup;
  infosGroup: FormGroup;

  /**
   * @param FormGroup generalGroup
   * @param FormGroup infosGroup
   */
  roomBookingForm: FormGroup;
  servicesGroup: FormGroup;


  private _existingServiceBookings: RoomBookingServiceBooking[];

  private _roomNotAvailable = false;

  private _otherServiceBookings: ServiceBooking[];

  private _owner: FormControl = new FormControl('', [
    Validators.required,
  ]);

  private _referenceOpeningDays: BuildingOpeningDay[];

  // as a dependency to allow the room Validation, to initialize
  private _referenceRooms: Room[];

  private _referenceServices: Service[];

  private _room: FormControl = new FormControl('', [
    Validators.required,
  ]);

  constructor(
    private formBuilder: FormBuilder,
    private validators: RoomBookingValidators,
  ) {
    this.generalGroup = this.formBuilder.group({
      id: null,
      room: this._room,
      owner: this._owner,
      guestsNumber: ['', [Validators.required, Validators.min(1)]],
      isInvoiceable: ['', [Validators.required]],
      startDate: ['', [Validators.required, outOfOpeningDays(this._referenceOpeningDays), datePastValidator()]],
      endDate: ['', [Validators.required, outOfOpeningDays(this._referenceOpeningDays), datePastValidator()]],
    }, {
      validators: [this.roomTooSmall, this.datesConflict],
    });
    this.infosGroup = this.formBuilder.group({
      name: ['', Validators.required],
      speakerName: ['', Validators.required],
    });
    this.servicesGroup = this.formBuilder.group({
      days: this.formBuilder.array([]),
    });
    this.roomBookingForm = this.formBuilder.group({
      generalGroup: this.generalGroup,
      infosGroup: this.infosGroup,
      servicesGroup: this.servicesGroup,
    });

    this.generalGroup.setAsyncValidators(this.validators.checkRoomEmpty(this.generalGroup));
  }

  /**
   * Adds missing services to days form array
   *
   * @param days Form array of days
   */
  addMissingServices(days: FormArray): FormArray {
    for (const day of days.controls) {
      for (const service of this._referenceServices) {
        let serviceAlreadyInDay = false;
        for (const booking of (day.get('serviceBookings') as FormArray).controls) {
          if (booking.get('service').value === service.name) {
            serviceAlreadyInDay = true;
          }
        }

        if (serviceAlreadyInDay === false) {
          const serviceBookings = (day.get('serviceBookings') as FormArray).controls;
          serviceBookings.push(this.createEmptyServiceBooking());
          serviceBookings[serviceBookings.length - 1].patchValue({
            quantity: 0,
            service: service.name,
            date: day.get('date').value,
          });
        }
      }
      ((day.get('serviceBookings') as FormArray).controls).sort((elem1, elem2) =>
        elem1.get('service').value > elem2.get('service').value ? 1 : -1);
    }

    return days;
  }

  createEmptyDay() {
    return this.formBuilder.group({
      date: ['', Validators.required],
      serviceBookings: this.formBuilder.array([]),
    });
  }

  createEmptyServiceBooking(service?: Service) {
    return this.formBuilder.group({
      date: ['', Validators.required],
      service: '',
      quantity: [{ value: '', disabled: service != null && service.isAddedOnRoomBooking},
        [Validators.required, Validators.min(0), this.quantityLimitExceeded, this.quantityGuessExceeded]],
    });
  }

  emitServicesSubmitEvent() {
    this.servicesGroupSubmitted.emit();
  }

  getFormFromRoomBooking(roomBooking: RoomBooking): FormGroup {
    Object.keys(this.roomBookingForm.get('generalGroup').value).forEach(key => {
      switch (key) {
        case 'room':
          this.room = roomBooking[key] as Room;
          break;
        case 'owner':
          this.owner = roomBooking[key] as Member;
          break;
        case 'startDate':
        case 'endDate':
          let formValue = moment(roomBooking[key]).format();
          formValue = formValue.substring(0, formValue.length - 6);
          this.roomBookingForm.get('generalGroup')['controls'][key].patchValue(formValue);
          break;
        default:
          this.roomBookingForm.get('generalGroup')['controls'][key].patchValue(roomBooking[key]);
          break;
      }
    });

    Object.keys(this.roomBookingForm.get('infosGroup').value).forEach(key => {
      this.roomBookingForm.get('infosGroup')['controls'][key].patchValue(roomBooking[key]);
    });

    Object.keys(this.roomBookingForm.get('servicesGroup').value).forEach(key => {
      if (key === 'days') {
        this.getFormFromServiceBookings(roomBooking);
      } else {
        this.roomBookingForm.get('servicesGroup')['controls'][key].patchValue(roomBooking[key]);
      }
    });

    return this.roomBookingForm;
  }

  getRoomBookingFromForm(
    form: FormGroup,
    roomBooking: RoomBooking = new RoomBooking(),
    servicesAsIris: boolean,
  ): RoomBooking {
    Object.keys(form).forEach(formGroup => {
      Object.keys(form[formGroup]).forEach(formControl => {
        switch (formControl) {
          case 'room':
            // @ts-ignore
            roomBooking['room'] = '/api/rooms/' + this.room.id;
            break;
          case 'startDate':
          case 'endDate':
            roomBooking[formControl] = new Date(form[formGroup][formControl]).toISOString();
            break;
          case 'days':
            roomBooking.serviceBookings = this.getServiceBookingsFromForm(form, servicesAsIris);
            break;
          case 'owner':
            roomBooking['owner'] = this.owner['@id'];
            break;
          default:
            roomBooking[formControl] = form[formGroup][formControl];
            break;
        }
      });
    });

    return roomBooking;
  }

  /**
   * Returns a service based on the value passed in parameter and the reference services passed to this form service
   *
   * @param serviceName
   */
  public getServiceFromName(serviceName: string): Service {
    return this._referenceServices.find(service => service.name === serviceName);
  }

  public getGroupingService(services: ServiceInvoiceData[]): ServiceInvoiceData[] {
    return services.sort((service1, service2) =>
      service1.service > service2.service ? -1 : 1)
      .reduce((base, elem) => {
        const last = base[base.length - 1];
        if (last != null && last.service === elem.service) {
          last.quantity += elem.quantity;
          return base;
        } else {
          return [...base, elem];
        }
      }, []);
  }

  /**
   * Adds missing days in the days FormArray
   * This is useful when editing an existing room booking
   *
   * @param days Days form array
   * @param roomBooking Room booking used to check dates
   */
  private addMissingDays(days: FormArray, roomBooking: RoomBooking): FormArray {
    const startDate = new Date(roomBooking.startDate);
    const endDate = new Date(roomBooking.endDate);

    for (const date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      let isDayAlreadyInForm = false;
      for (const day of days.value) {
        if (moment(day.date).isSame(date, 'day') === true) {
          isDayAlreadyInForm = true;
        }
      }

      if (isDayAlreadyInForm === false) {
        days.push(this.createEmptyDay());
        days.at(days.length - 1).patchValue({date: date.toDateString()});
      }
    }

    return days;
  }

  /**
   * Validators that triggers if the selected dates are in conflict with another room booking dates
   *
   * @param formGroup The form group that contain the dates formControl
   * @return ValidationErrors if there is a conflict, null otherwise
   */
  private datesConflict: ValidatorFn = (): ValidationErrors | null => {
    return this._roomNotAvailable ? {datesConflict: true} : null;
  };

  private getFormFromServiceBookings(roomBooking: RoomBooking) {
    const bookings = roomBooking.serviceBookings as RoomBookingServiceBooking[];
    const days: FormArray = this.roomBookingForm.get('servicesGroup')['controls']['days'];

    this.addMissingDays(days, roomBooking);

    if (bookings.length > 0) {
      for (const currentDay of days.controls) {
        const serviceBookings = currentDay.get('serviceBookings') as FormArray;

        for (const booking of bookings) {
          if (moment(booking.date).isSame(currentDay.get('date').value, 'day') === true) {
            serviceBookings.push(this.createEmptyServiceBooking());
            const serviceBooking = serviceBookings.at(serviceBookings.length - 1);

            serviceBooking.patchValue({
              quantity: booking.quantity,
              service: (booking.service as Service).name,
              date: booking.date,
            });
          }
        }
      }
    }
    return days;
  }

  /**
   * Gets the model of the services bookings form a form
   *
   * @param roomBookingForm The form of the room booking
   */
  private getServiceBookingsFromForm(roomBookingForm: FormGroup, servicesAsIri: boolean) {
    const serviceBookings: RoomBookingServiceBooking[] = [];

    for (const day of (this.servicesGroup.get('days') as FormArray).controls) {
      for (const serviceBooking of (day.get('serviceBookings') as FormArray).controls) {
        if (serviceBooking.get('quantity').value > 0) {
          const service: Service = this.getServiceFromName(serviceBooking.get('service').value);
            serviceBookings.push({
              date: serviceBooking.get('date').value,
              quantity: serviceBooking.get('quantity').value,
              service: servicesAsIri ? service['@id'] : service,
            });
        }
      }
    }

    return serviceBookings;
  }

  /**
   * Validator that triggers if a day exceeds the quantity limit of a service
   *
   * @param control
   */
  private quantityLimitExceeded: ValidatorFn = (control: FormControl): { [key: string]: any } | null => {
    const serviceBooking = control.parent;
    const service: Service = serviceBooking ? this.getServiceFromName(serviceBooking.get('service').value) : null;

    if (!this._otherServiceBookings || !this._referenceServices || !serviceBooking || !service) {
      return null;
    }

    const maximumQuantity: number = service.quantityLimitAdmin;
    let quantityAlreadyBooked = 0;

    for (const otherBooking of this.otherServiceBookings) {
      if (service['@id'] === otherBooking.service &&
        moment(serviceBooking.get('date').value).isSame(otherBooking.date, 'day')) {
        quantityAlreadyBooked += otherBooking.quantity;
      }
    }

    if (quantityAlreadyBooked + serviceBooking.get('quantity').value > maximumQuantity) {
      return {quantityLimitExceeded: true};
    }

    return null;
  };

  private quantityGuessExceeded: ValidatorFn = (control: FormControl): { [key: string]: any } | null => {
    const maxGuestsNumber = this.generalGroup.get('guestsNumber').value;
    const serviceBooking = control.parent;
    const service: Service = serviceBooking ? this.getServiceFromName(serviceBooking.get('service').value) : null;

    if (!this._otherServiceBookings || !this._referenceServices || !serviceBooking || !service) {
      return null;
    }

    if (serviceBooking.get('quantity').value > maxGuestsNumber) {
      return {quantityGuessExceeded: true};
    }

    return null;
  };

  /**
   * Validator that triggers if guests number exceeds room capacity
   *
   * @param formGroup
   */
  private roomTooSmall: ValidatorFn = (formGroup: FormGroup): { [key: string]: any } | null => {
    if (!this.referenceRooms || !this.room || !formGroup.get('guestsNumber') || !this.room.capacity) {
      return null;
    }

    return formGroup.get('guestsNumber').value > this.room.capacity ? {roomTooSmall: true} : null;
  };
}

